// Buscador de productos del catálogo (publicListProducts): agrega SKUs como chips y los quita.
import { useState } from 'react';
import { X } from 'lucide-react';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { Input } from '@/kit';
import { formatCents } from '../lib/promotionsRow';
import type { SourceState } from '../api/usePromotionFormSources';

const MIN_QUERY = 2;
const MAX_RESULTS = 8;

export interface ProductSearchPickerProps {
    label: string;
    products: CatalogProduct[];
    state: SourceState;
    selected: string[];
    onChange: (skus: string[]) => void;
    placeholder: string;
    error?: string;
}

function productLabel(product: CatalogProduct): string {
    return [product.sku, [product.brand, product.model].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(' · ');
}

function matches(product: CatalogProduct, query: string): boolean {
    const text = [product.sku, product.brand, product.model, product.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return text.includes(query);
}

export function ProductSearchPicker({
    label,
    products,
    state,
    selected,
    onChange,
    placeholder,
    error,
}: ProductSearchPickerProps) {
    const [query, setQuery] = useState('');
    const needle = query.trim().toLowerCase();
    const results =
        needle.length < MIN_QUERY
            ? []
            : products
                  .filter((product) => !selected.includes(product.sku) && matches(product, needle))
                  .slice(0, MAX_RESULTS);
    const bySku = new Map(products.map((product) => [product.sku, product]));

    return (
        <div className="space-y-1.5">
            {selected.length > 0 ? (
                <ul aria-label={`${label} elegidos`} className="flex flex-wrap gap-1">
                    {selected.map((sku) => {
                        const product = bySku.get(sku);
                        return (
                            <li
                                key={sku}
                                className="flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px]"
                            >
                                {product ? productLabel(product) : sku}
                                <button
                                    type="button"
                                    aria-label={`Quitar ${sku}`}
                                    onClick={() => {
                                        onChange(selected.filter((item) => item !== sku));
                                    }}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            <Input
                type="search"
                aria-label={label}
                placeholder={placeholder}
                value={query}
                autoComplete="off"
                error={error}
                disabled={state === 'unavailable'}
                hint={
                    state === 'unavailable'
                        ? 'No pudimos leer el catálogo de productos; recarga la pantalla o intenta más tarde.'
                        : undefined
                }
                onChange={(event) => {
                    setQuery(event.target.value);
                }}
            />
            {needle.length >= MIN_QUERY ? (
                <ul
                    aria-label={`Resultados de ${label}`}
                    className="rounded-md border border-border"
                >
                    {state === 'loading' ? (
                        <li className="px-2 py-1 text-[11px] text-muted-foreground">Buscando…</li>
                    ) : results.length === 0 ? (
                        <li className="px-2 py-1 text-[11px] text-muted-foreground">
                            Sin resultados
                        </li>
                    ) : (
                        results.map((product) => (
                            <li key={product.sku}>
                                <button
                                    type="button"
                                    className="flex w-full justify-between px-2 py-1 text-left text-[11px] hover:bg-muted/40"
                                    onClick={() => {
                                        onChange([...selected, product.sku]);
                                        setQuery('');
                                    }}
                                >
                                    <span>{productLabel(product)}</span>
                                    <span className="text-muted-foreground">
                                        {formatCents(product.basePriceCents)}
                                    </span>
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            ) : null}
        </div>
    );
}
